import type { PoiCategory, PoiData } from '$lib/components/pois/types';
import type { PoisStoreContent } from '../../../../types';
import type { PageLoad } from './$types';
import { getPOIStore } from '$houdini';
import { getBoundsFromPoi } from '$lib/components/pois/utils';
import { isGraphQLError } from '$lib/errors/graphql';
import { routesPagename } from '$lib/services/urls/routesPagename';
import { error, type LoadEvent } from '@sveltejs/kit';
import { derived, get, type Readable } from 'svelte/store';
import {
	enrichPoisStoreWithFetchedPoiData,
	getPoiFilters,
	getPoisStore,
} from '../../../../loadPois';
import { getPageSeoData } from './metas';

interface GetPoiArgs {
	id: string;
	category: PoiCategory;
	poisStore: Readable<PoisStoreContent | null>;
	event: LoadEvent;
}

async function getPoiData({
	id,
	category,
	poisStore,
	event,
}: GetPoiArgs): Promise<PoiData | undefined> {
	try {
		const { searchParams } = await event.parent();
		const cachedPoi = get(poisStore)?.poisList.find(poi => poi.id === id);
		const filters = searchParams.filters;

		if (!cachedPoi) {
			// eslint-disable-next-line new-cap
			const getPOI = new getPOIStore();
			await getPOI
				.fetch({
					event,
					variables: {
						input: {
							id,
							type: category,
							poiFilters: getPoiFilters(filters),
						},
					},
					blocking: true,
				})
				.catch((error) => {
					// Workaround to make sure we subscribe and unsubscribe after an error is thrown
					// This avoids a memory leak coming from the houdini store
					//
					// See https://github.com/HoudiniGraphql/houdini/issues/1294
					get(getPOI);

					throw error;
				});

			return get(getPOI).data?.getPOIFromId.poi;
		}

		return cachedPoi;
	} catch (e) {
		if (isGraphQLError(e) && e.code === 'POI_ID_NOT_FOUND') {
			return undefined;
		}

		throw e;
	}
}

export const load = (async (event) => {
	const { poisStore: cachedPoisStore, lang, searchParams, searchPOIStore, searchPOIFromAddressStore } = await event.parent();
	const cachedPoiData = get(cachedPoisStore);
	const { page, filters } = searchParams;

	const { poi, category } = event.params;

	if (!category) {
		return error(404);
	}

	const poiParts = poi.split('-');
	const poiId = poiParts[poiParts.length - 1];

	const poiData = await getPoiData({
		id: poiId,
		category,
		poisStore: cachedPoisStore,
		event,
	});

	if (!poiData) {
		return error(404);
	}

	const poisStoreWithoutSelectedPoiData = await getPoisStore({
		event,
		page,
		searchParams,
		searchBounds: cachedPoiData ? cachedPoiData.mapBounds : getBoundsFromPoi(poiData),
		cachedPoisStore,
		searchPOIStore,
		searchPOIFromAddressStore,
	});

	const poisStore = enrichPoisStoreWithFetchedPoiData(poisStoreWithoutSelectedPoiData, poiData);

	// Fetch the POI data for SEO
	// We do a fetch again compared to above because the POI data may have been cached
	// The cached version of the poi data does not contain the locationLocalizedHierarchies
	//
	// If this load is called after another one before, the cache will be used
	// eslint-disable-next-line new-cap
	const getPOI = new getPOIStore();
	await getPOI
		.fetch({
			event,
			variables: {
				input: {
					id: poiId,
					type: category,
					poiFilters: getPoiFilters(filters),
				},
			},
			// Set blocking to false to avoid blocking the page load
			// In case of a internal navigation, we don't need to wait for the seo data to be loaded
			blocking: false,
		})
		.catch((error) => {
			// Workaround to make sure we subscribe and unsubscribe after an error is thrown
			// This avoids a memory leak coming from the houdini store
			//
			// See https://github.com/HoudiniGraphql/houdini/issues/1294
			get(getPOI);

			throw error;
		});

	const seoDataStore = derived(getPOI, ($getPOI) => {
		const poiFromId = $getPOI?.data?.getPOIFromId;

		return getPageSeoData(event, {
			poiFromId,
			category,
			lang,
		});
	});

	return {
		page,
		getPOIStore: getPOI,
		poisStore,
		seoDataStore,
		mobileTopButtons: ['search'],
		pagename: routesPagename[`${category}_DATASHEET`],
	};
}) satisfies PageLoad;
